import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PasswordInput from "../components/PasswordInput";
import { API } from "../context/AuthContext";
import { useSeo } from "../lib/seo";

/**
 * Code-only password reset (BookFreely).
 *
 * Flow (matches backend security spec):
 *   1. User enters email → POST /auth/forgot-password/code (neutral response).
 *   2. BookFreely emails a 6-digit code (10-minute expiry, backend deletes it
 *      after successful use, older codes invalidated on resend).
 *   3. User enters the code → moves to the "set new password" step (client-side
 *      only — the code is validated server-side alongside the new password).
 *   4. New password is submitted → POST /auth/forgot-password/code/verify.
 *      On success: password changed, code deleted, active sessions revoked
 *      (JWT `iat` older than `password_updated_at` is rejected), user must
 *      sign in again.
 *
 * The email-link flow was removed entirely — this page is the only entry point.
 */
export default function ForgotPassword() {
  useSeo({
    title: "Reset your BookFreely password",
    description: "Reset your BookFreely account password using a one-time 6-digit code sent to your email.",
    path: "/forgot-password",
    noindex: true,
  });
  // Two-step wizard: "email" → "verify" (enter code + new password) → "done".
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0); // seconds until next resend allowed

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const sendCode = async () => {
    if (cooldown > 0 || busy) return;
    if (!email.trim()) {
      setError("Enter the email address for your BookFreely account.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      // Neutral response — succeeds even for unknown emails to avoid enumeration
      await axios.post(`${API}/auth/forgot-password/code`, { email: email.trim() });
      setStep("verify");
      setCooldown(60);
    } catch (err) {
      // Neutral response → still advance UI so we don't leak account existence
      setStep("verify");
      setCooldown(60);
    } finally {
      setBusy(false);
    }
  };

  const resendCode = async () => {
    if (cooldown > 0 || busy) return;
    setError("");
    setBusy(true);
    try {
      await axios.post(`${API}/auth/forgot-password/code/resend`, { email: email.trim() });
    } catch (_) {
      // Neutral
    }
    setCooldown(60);
    setBusy(false);
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setError("");
    if (!code.trim() || code.trim().length !== 6) {
      setError("Enter the 6-digit code we emailed you.");
      return;
    }
    if (newPassword.length < 8) {
      setError("Your new password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("The two passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/forgot-password/code/verify`, {
        email: email.trim(),
        code: code.trim(),
        new_password: newPassword,
      });
      setStep("done");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string" && detail.trim()
          ? detail
          : "Could not reset your password. Double-check the code and try again."
      );
    } finally {
      setBusy(false);
    }
  };

  const restart = () => {
    setStep("email");
    setCode("");
    setNewPassword("");
    setConfirmPassword("");
    setError("");
    setCooldown(0);
  };

  const inputCls =
    "mt-1 w-full bg-transparent border rounded-md px-3 py-2 outline-none focus:border-[var(--gf-accent)]";
  const inputStyle = { borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" };

  return (
    <div className="App" data-testid="forgot-password-page">
      <Header hideSearch />
      <div className="max-w-md mx-auto px-4 sm:px-6 py-14">
        <div className="uppercase text-xs tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>
          Account recovery
        </div>
        <h1 className="font-display text-3xl sm:text-4xl mt-2" style={{ color: "var(--gf-text)" }}>
          Reset your password
        </h1>
        <p className="text-sm mt-3" style={{ color: "var(--gf-text-muted)" }}>
          We'll email you a 6-digit code. Enter the code to set a new password —
          the code expires in 10 minutes and can only be used once.
        </p>

        {/* Step 1: enter email */}
        {step === "email" && (
          <form
            className="mt-6 space-y-4"
            data-testid="forgot-step-email"
            onSubmit={(e) => { e.preventDefault(); sendCode(); }}
          >
            <div>
              <label htmlFor="forgot-email" className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Email</label>
              <input
                id="forgot-email"
                data-testid="forgot-email-input"
                required
                type="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className={inputCls}
                style={inputStyle}
              />
            </div>
            {error && <div data-testid="forgot-error" className="text-sm" style={{ color: "#B42318" }}>{error}</div>}
            <button
              data-testid="forgot-send-code-btn"
              type="submit"
              disabled={busy}
              className="w-full rounded-full font-medium py-3 disabled:opacity-50"
              style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
            >
              {busy ? "Sending…" : "Email me a code"}
            </button>
            <Link to="/login" className="block text-center text-xs mt-3 hover:underline" style={{ color: "var(--gf-text-muted)" }}>
              Back to sign in
            </Link>
          </form>
        )}

        {/* Step 2: enter code + new password */}
        {step === "verify" && (
          <form
            className="mt-6 space-y-4"
            data-testid="forgot-step-verify"
            onSubmit={submitReset}
          >
            <p className="text-sm rounded-lg border px-3 py-2" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface-2)", color: "var(--gf-text-muted)" }}>
              If a BookFreely account exists for <b>{email}</b>, we've emailed a 6-digit code.
            </p>
            <div>
              <label htmlFor="forgot-code" className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>6-digit code</label>
              <input
                id="forgot-code"
                data-testid="forgot-code-input"
                required
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                autoComplete="one-time-code"
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                className={inputCls + " tracking-[8px] text-center font-mono text-lg"}
                style={inputStyle}
              />
            </div>
            <div>
              <label htmlFor="forgot-new-password" className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>New password</label>
              <PasswordInput
                id="forgot-new-password"
                data-testid="forgot-new-password-input"
                required
                minLength={8}
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 8 characters, includes a symbol"
                className={inputCls}
                style={inputStyle}
              />
            </div>
            <div>
              <label htmlFor="forgot-confirm-password" className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Confirm new password</label>
              <PasswordInput
                id="forgot-confirm-password"
                data-testid="forgot-confirm-password-input"
                required
                minLength={8}
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={inputCls}
                style={inputStyle}
              />
            </div>
            {error && <div data-testid="forgot-error" className="text-sm" style={{ color: "#B42318" }}>{error}</div>}
            <button
              data-testid="forgot-submit-reset-btn"
              type="submit"
              disabled={busy}
              className="w-full rounded-full font-medium py-3 disabled:opacity-50"
              style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
            >
              {busy ? "Resetting…" : "Reset password"}
            </button>
            <div className="flex items-center justify-between text-xs mt-1">
              <button
                type="button"
                data-testid="forgot-resend-btn"
                disabled={cooldown > 0 || busy}
                onClick={resendCode}
                className="hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ color: "var(--gf-text-muted)" }}
              >
                {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
              </button>
              <button
                type="button"
                onClick={restart}
                data-testid="forgot-change-email-btn"
                className="hover:underline"
                style={{ color: "var(--gf-text-muted)" }}
              >
                Use a different email
              </button>
            </div>
          </form>
        )}

        {/* Step 3: success */}
        {step === "done" && (
          <div
            data-testid="forgot-done"
            className="mt-8 rounded-2xl border p-6"
            style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}
          >
            <div className="font-display text-xl" style={{ color: "var(--gf-text)" }}>Password updated</div>
            <p className="text-sm mt-2" style={{ color: "var(--gf-text-muted)" }}>
              Your password has been changed and every existing session has been
              signed out. You can sign in now with your new password.
            </p>
            <Link
              to="/login"
              data-testid="forgot-signin-link"
              className="inline-block mt-5 rounded-full px-5 py-2 text-sm font-medium"
              style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
            >
              Go to sign in
            </Link>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
